import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { fmtMoney, fmtNumber } from './format';
import { c, t, sp, r, chart } from '../../theme';

// How the on-hand value divides across the workbook's aged categories.
//
// A pie compares every slice with every other, so the palette has to be
// `chart.coVisible` — the all-pairs-validated set — and NOT `categorical`,
// which is only checked for adjacent pairs and puts two hues 1.3 ΔE apart
// under deuteranopia. The three aged categories take coVisible slots and the
// remainder takes the neutral: it is the one slice nobody needs to tell apart
// from another category, only from "aged".
//
// Three slices, not four. Non-Stock 45+ was the fourth until the overlap was
// measured — it shares 255 lines with the aged bands, so as a slice it claimed
// a share of a whole it partly duplicates. It moved to the footnote below,
// beside Over Max, which has always been handled that way for the same reason.
const SEGMENTS = [
  { key: 'aged_6_12',         label: 'Aged 6–12 mo',          fill: chart.coVisible[0] },
  { key: 'aged_12_plus',      label: 'Aged +12 mo',           fill: chart.coVisible[1] },
  { key: 'critical_spare_12', label: 'Critical Spare +12 mo', fill: chart.coVisible[2] },
  { key: 'not_aged',          label: 'Not Aged',              fill: chart.other },
];

// How many of SEGMENTS are aged categories — the remainder is the tail entry.
const AGED_SEGMENTS = SEGMENTS.length - 1;

export default function CategoryCompositionBar({ kpis, byCategory }) {
  const total = Number(kpis?.total_value || 0);
  if (!total || !byCategory?.length) return null;

  // These slices genuinely partition — every line lands in exactly one — so
  // they sum to on-hand value and each slice's share is a real share. That is
  // what a pie asserts, and it is now true rather than arranged: the categories
  // left in it are the three the source data proves are mutually exclusive.
  const value = Object.fromEntries(byCategory.map((row) => [row.category, Number(row.value || 0)]));
  const lines = Object.fromEntries(byCategory.map((row) => [row.category, Number(row.item_count || 0)]));

  const data = SEGMENTS
    .map((seg) => ({ ...seg, value: value[seg.key] || 0, lines: lines[seg.key] || 0 }))
    .filter((seg) => seg.value > 0);

  const aged = SEGMENTS.slice(0, AGED_SEGMENTS).reduce((sum, seg) => sum + (value[seg.key] || 0), 0);

  return (
    <div>
      <div style={s.head}>
        <div style={s.title}>Where the Value Sits</div>
        <div style={s.sub}>
          {fmtMoney(aged)} — {((aged / total) * 100).toFixed(1)}% of on hand — is in an aged category
        </div>
      </div>

      <div style={s.body}>
        <div style={s.chartWrap}>
          <ResponsiveContainer width="100%" height={210}>
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="label"
                innerRadius={54}
                outerRadius={84}
                paddingAngle={2}
                isAnimationActive={false}
              >
                {data.map((seg) => (
                  <Cell key={seg.key} fill={seg.fill} stroke={c.surface} strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip
                formatter={(v, n) => [`${fmtMoney(v)} · ${((v / total) * 100).toFixed(1)}%`, n]}
                contentStyle={{ fontSize: '12px', borderRadius: r.lg, border: `1px solid ${chart.tooltipBorder}` }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* A table, not a flex row: the numbers have to line up in columns, and
            a flex legend collapses them into each other at narrow widths. */}
        <div style={s.legendWrap}>
          <table style={s.legend}>
            <tbody>
              {data.map((seg) => (
                <tr key={seg.key}>
                  <td style={s.swatchCell}>
                    <span style={{ ...s.swatch, background: seg.fill }} />
                  </td>
                  <td style={s.labelCell}>{seg.label}</td>
                  <td style={s.pctCell}>{((seg.value / total) * 100).toFixed(1)}%</td>
                  <td style={s.valCell}>{fmtMoney(seg.value)}</td>
                  <td style={s.lineCell}>{fmtNumber(seg.lines)}</td>
                </tr>
              ))}
              <tr>
                <td style={s.totalRule} />
                <td style={{ ...s.totalRule, ...s.totalLabel }}>All On Hand</td>
                <td style={{ ...s.totalRule, ...s.pctCell, fontWeight: 700 }}>100%</td>
                <td style={{ ...s.totalRule, ...s.valCell, ...s.totalVal }}>{fmtMoney(total)}</td>
                <td style={{ ...s.totalRule, ...s.lineCell }}>{fmtNumber(kpis.item_count)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Both of these cut ACROSS the slices above rather than sitting beside
          them, so neither can be one. A part can be over its maximum and aged;
          a part can carry a Non-Stock 45+ value and an aged value. Putting
          either in the pie counts the overlap twice and the slices stop making
          up the whole.

          Non-Stock 45+ is quoted here at its full column total. Inside the pie
          it could only ever have shown the 369 lines that overlap nothing —
          $219,408 of $338,825 — while the KPI tile showed all of it. */}
      <div style={s.footnote}>
        <span style={s.footLabel}>Non Stock Aged +45 days</span>
        <span style={s.footValue}>{fmtMoney(kpis.non_stock_45_days_value)}</span>
        <span style={s.footNote}>
          shown apart — {fmtNumber(kpis.non_stock_45_days_count)} lines, of which 255 also carry an aged
          band above, so it is not a share of the whole
        </span>
      </div>
      <div style={s.footnote}>
        <span style={s.footLabel}>Over Max</span>
        <span style={s.footValue}>{fmtMoney(kpis.over_max_value)}</span>
        <span style={s.footNote}>
          shown apart — it cuts across these categories rather than sitting beside them, so a part
          can be both
        </span>
      </div>
    </div>
  );
}

const s = {
  head:  { padding: `${sp[4]} ${sp[5]} 0` },
  title: { ...t.h3, color: c.navy },
  sub:   { ...t.micro, color: c.textFaint, marginTop: '2px' },

  // Side by side where there is room, stacking below ~620px so the legend
  // keeps its full width rather than being squeezed against the chart.
  body: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: sp[3], padding: `${sp[3]} ${sp[5]} ${sp[4]}`, alignItems: 'center',
  },
  chartWrap:  { minWidth: 0 },
  legendWrap: { minWidth: 0, overflowX: 'auto' },
  legend:     { width: '100%', borderCollapse: 'collapse', ...t.caption },

  swatchCell: { width: '18px', padding: '5px 0', verticalAlign: 'middle' },
  swatch:     { display: 'block', width: '10px', height: '10px', borderRadius: '2px' },
  labelCell:  { padding: '5px 10px 5px 0', color: c.textStrong, whiteSpace: 'nowrap' },
  pctCell:    { padding: '5px 10px', ...t.tabular, fontWeight: 700, color: c.text, textAlign: 'right', whiteSpace: 'nowrap' },
  valCell:    { padding: '5px 10px', ...t.tabular, color: c.textMuted, textAlign: 'right', whiteSpace: 'nowrap' },
  lineCell:   { padding: '5px 0 5px 10px', ...t.tabular, color: c.textFaint, textAlign: 'right', whiteSpace: 'nowrap' },
  totalRule:  { borderTop: `1px solid ${c.border}`, paddingTop: '8px' },
  totalLabel: { fontWeight: 700, color: c.text, whiteSpace: 'nowrap' },
  totalVal:   { fontWeight: 700, color: c.text },

  footnote: {
    display: 'flex', alignItems: 'baseline', gap: sp[2], flexWrap: 'wrap',
    padding: `${sp[3]} ${sp[5]}`, borderTop: `1px solid ${c.border}`, background: c.surfaceSunk,
  },
  footLabel: { ...t.caption, fontWeight: 700, color: c.textStrong },
  footValue: { ...t.caption, ...t.tabular, fontWeight: 600, color: c.text },
  footNote:  { ...t.micro, color: c.textFaint },
};
